import { useSelector, useDispatch } from 'react-redux';
import { Autocomplete, TextField, Tooltip, Skeleton } from '@mui/material';
import { Info } from '@mui/icons-material';
import { useEffect, useCallback, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';

import { useQuery } from '@tanstack/react-query';

import { axiosPrivate } from 'app/utils';
import {
  SELECTED_PHARMACY_ID_KEY,
  changeSelectedPharmacy,
  fetchUserPharmacies,
} from './store/pharmacySelectorSlice';

const SELECTED_ORGANISATION_ID_KEY = 'selected_organisation_id';
const MAX_PHARMACIES_FETCH_ATTEMPTS = 3;

async function getOrganisationsForPharmacist(pharmacistId) {
  const response = await axiosPrivate.get(`/pharmacists/${pharmacistId}/organisations`);
  return response.data.data?.organisations?.sort((a, b) => a.name.localeCompare(b.name)) ?? [];
}

function OrganisationSelector() {
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();

  const selectedOrganisationId = searchParams.get('organisationId') || '';
  const { user } = useSelector((state) => state.auth);
  const {
    pharmacies,
    selectedPharmacy,
    fetching: pharmaciesFetching,
  } = useSelector((state) => state.pharmacySelector);
  const pharmaciesFetchAttempts = useRef(0);

  const { data: organisations, isLoading: isOrganisationsLoading } = useQuery({
    queryKey: ['pharmacists', user?._id, 'organisations'],
    queryFn: () => getOrganisationsForPharmacist(user?._id),
    enabled: Boolean(user?._id),
    staleTime: 5 * 60 * 1000, // 5 minutes
    meta: { errorMessage: 'Failed to load organisations' },
  });

  const selectedOrganisation = useMemo(() => {
    return organisations?.find((org) => org._id === selectedOrganisationId) || null;
  }, [organisations, selectedOrganisationId]);

  const setSelectedOrganisationId = useCallback(
    (organisationId) => {
      localStorage.setItem(SELECTED_ORGANISATION_ID_KEY, organisationId);
      setSearchParams(
        (prev) => {
          prev.set('organisationId', organisationId);
          return prev;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  useEffect(() => {
    if (!organisations?.length) return;

    if (selectedOrganisationId) {
      const isAvailableOrganisation = organisations.some(
        (organisation) => organisation._id === selectedOrganisationId
      );
      if (isAvailableOrganisation) {
        localStorage.setItem(SELECTED_ORGANISATION_ID_KEY, selectedOrganisationId);
      }
      return;
    }

    // Without one in the URL (as from the sidebar) the organisation of the selected pharmacy opens,
    // so this page and the rest of Connect agree. Before any pharmacy is selected (a fresh load), the
    // user's pharmacies are awaited to look up the saved one. A failed fetch is retried below, so no
    // fallback is put in the URL while a retry could still succeed (it could not replace it); once
    // the last attempt has failed, the remembered organisation is used.
    const awaitingPharmacies =
      pharmacies === undefined &&
      (pharmaciesFetching || pharmaciesFetchAttempts.current < MAX_PHARMACIES_FETCH_ATTEMPTS);
    if (!selectedPharmacy && awaitingPharmacies) return;

    const savedPharmacyId = localStorage.getItem(SELECTED_PHARMACY_ID_KEY);
    const pharmacyOrganisationId =
      selectedPharmacy?.chain_id ??
      pharmacies?.find((pharmacy) => pharmacy._id === savedPharmacyId)?.chain_id;
    const rememberedOrganisationId = localStorage.getItem(SELECTED_ORGANISATION_ID_KEY);
    const defaultOrganisation =
      organisations.find((organisation) => organisation._id === pharmacyOrganisationId) ||
      organisations.find((organisation) => organisation._id === rememberedOrganisationId);
    setSelectedOrganisationId(defaultOrganisation?._id || organisations[0]._id);
  }, [
    organisations,
    selectedOrganisationId,
    selectedPharmacy,
    pharmacies,
    pharmaciesFetching,
    setSelectedOrganisationId,
  ]);

  // Ensure pharmacies are loaded — they may not be if PharmacySelector isn't mounted.
  // Only dispatch when pharmacies haven't been fetched yet (undefined), not when the
  // API returned an empty list (empty array). A failing request is retried a few times, not forever.
  useEffect(() => {
    if (
      pharmacies === undefined &&
      !pharmaciesFetching &&
      user?._id &&
      pharmaciesFetchAttempts.current < MAX_PHARMACIES_FETCH_ATTEMPTS
    ) {
      pharmaciesFetchAttempts.current += 1;
      dispatch(fetchUserPharmacies(user._id));
    }
  }, [pharmacies, pharmaciesFetching, user?._id, dispatch]);

  // The sidebar follows the selected pharmacy, so it is moved into the organisation in view — but
  // only when it sits outside it, never swapped for another branch of the same organisation. It
  // moves when the organisation changes, not the pharmacy: a pharmacy picked just before coming
  // here can land after this page opened, and is not pulled back.
  useEffect(() => {
    if (!selectedOrganisationId || !pharmacies?.length) return;
    if (selectedPharmacy?.chain_id === selectedOrganisationId) return;

    const organisationPharmacies = pharmacies.filter(
      (pharmacy) => pharmacy.chain_id === selectedOrganisationId
    );
    const rememberedPharmacyId = localStorage.getItem(SELECTED_PHARMACY_ID_KEY);
    const pharmacy =
      organisationPharmacies.find(({ _id }) => _id === rememberedPharmacyId) ||
      organisationPharmacies.sort((a, b) => a.pharmacy_name.localeCompare(b.pharmacy_name))[0];

    if (pharmacy) {
      dispatch(changeSelectedPharmacy(pharmacy._id));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOrganisationId, pharmacies, dispatch]);

  function handleOrganisationChange(event, newValue) {
    if (newValue) {
      setSelectedOrganisationId(newValue._id);
    }
  }

  function filterOptions(options, { inputValue }) {
    const lowerInput = inputValue.toLowerCase();
    return options.filter((option) => option.name.toLowerCase().startsWith(lowerInput));
  }

  if (isOrganisationsLoading && !organisations) {
    return <Skeleton variant="rectangular" width={320} height={40} />;
  }

  if (organisations?.length === 0) return null;

  if (organisations?.length === 1) {
    return <span className="text-16 font-600 px-16 text-white">{organisations[0].name}</span>;
  }

  return (
    <div className="flex items-center">
      <Autocomplete
        value={selectedOrganisation}
        onChange={handleOrganisationChange}
        options={organisations || []}
        getOptionLabel={(option) => option.name}
        filterOptions={filterOptions}
        size="small"
        className="min-w-[320px]"
        sx={autocompleteStyles}
        renderInput={(params) => (
          <TextField
            {...params}
            className="font-600 text-14 text-dark bg-white rounded-lg"
            sx={textFieldStyles}
          />
        )}
        renderOption={(props, option) => (
          <li {...props} key={option._id}>
            <span className="mr-6">{option.name}</span>
          </li>
        )}
      />
      <Tooltip
        title="If you belong to multiple organisations, you can switch between your organisations from this drop-down list"
        placement="right"
      >
        <Info className="text-white ml-10" />
      </Tooltip>
    </div>
  );
}

const autocompleteStyles = {
  '& .MuiOutlinedInput-root': {
    boxShadow: 'none',
    '& fieldset': {
      borderWidth: 0,
    },
    '&:hover fieldset': {
      borderWidth: 0,
    },
    '&.Mui-focused fieldset': {
      borderWidth: 0,
    },
  },
};

const textFieldStyles = {
  '& .MuiOutlinedInput-root': {
    boxShadow: 'none',
    '& fieldset': {
      borderWidth: 0,
    },
    '&:hover fieldset': {
      borderWidth: 0,
    },
    '&.Mui-focused fieldset': {
      borderWidth: 0,
    },
  },
};

export default OrganisationSelector;
